import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  CUSTOM_COLORS_KEYS,
  getAppTitle,
  GetColor,
  Headline3,
  Notifications,
  NotificationType,
  type VennColors,
  ZIndex,
  type CustomColors,
  Tooltip,
  type CustomColorsKeys,
} from 'venn-ui-kit';
import styled, { ThemeContext } from 'styled-components';
import ModalHeader from '../modal/ModalHeader';
import ModalFooter from '../modal/ModalFooter';
import { ModalContent } from '../modal/components/ModalWrapper';
import { cloneDeep, isEqual, isNil } from 'lodash';
import { Hotkeys, logExceptionIntoSentry, useHasFF, useHotkeys, useModal } from 'venn-utils';
import { ConditionalOverlay } from '../conditional-overlay';
import { UserContext } from '../contexts';
import { type ColorItem, InlineColorPicker } from './InlineColorPicker';

type ColorGroup = {
  colors: ColorItem[];
  children: {
    name: string;
    value: ColorGroup;
  }[];
};

interface ColorScheme {
  name: string;
  description: string | JSX.Element;
  colors: ColorGroup;
}
type EditableColorSetting<KEYS extends string | number | symbol> = {
  displayName: string;
  description: string | JSX.Element;
  /** Influences editability, display name, and also sets an implicit ordering based on the ordering of the editable keys. */
  editableKeys?: { key: KEYS; displayName?: string }[];
};

interface ColorPickerProps {
  close: () => void;
}

export const CustomThemeCustomizer = () => {
  const [isOpen, open, close] = useModal();
  const canOpen = useHasFF('color_scheme_picker');

  useHotkeys((event) => {
    if (canOpen) {
      open();
      event.preventDefault();
    }
  }, Hotkeys.COLOR_SCHEME_PICKER);

  return isOpen ? <PickerWarningStateWrapper close={close} /> : null;
};

const PickerWarningStateWrapper = ({ close }: ColorPickerProps) => {
  return <CustomThemeCustomizerInner close={close} />;
};

function usePublishCallback(onSuccess: () => void) {
  const { saveCustomColors } = useContext(UserContext);
  const [isPublishing, setIsPublishing] = useState(false);
  return {
    isPublishing,
    onPublish: async (customColors: CustomColors | undefined) => {
      setIsPublishing(true);
      try {
        await saveCustomColors(customColors);
        Notifications.notify('Color Scheme Saved', NotificationType.SUCCESS);
        onSuccess?.();
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        logExceptionIntoSentry(e);
        Notifications.notify('Color Scheme Failed to Save', NotificationType.ERROR);
      } finally {
        setIsPublishing(false);
      }
    },
  };
}

const CustomThemeCustomizerInner = ({ close }: ColorPickerProps) => {
  const { Colors, setCustomColors, customColors } = useContext(ThemeContext);
  const [originalColors] = useState(() => cloneDeep(Colors));
  const hasChanges = useMemo(() => {
    return !isEqual(Colors, originalColors);
  }, [Colors, originalColors]);
  const [hasPendingChange, setHasPendingChange] = useState(false);

  const [originalCustomColors] = useState(customColors);

  const schemes = useMemo(() => getSchemes(Colors), [Colors]);

  const { isPublishing: saving, onPublish } = usePublishCallback(close);
  const onSave = () => onPublish(customColors);

  const renderColorGroup = ({ colors, children }: ColorGroup) => {
    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', paddingTop: '10px' }}>
          {colors.map((color, idx) => {
            return (
              <Inline key={color.name} order={(idx * 2) % colors.length}>
                <InlineColorPicker color={color} setHasPendingChange={setHasPendingChange} />
              </Inline>
            );
          })}
        </div>
        {children.map(({ name, value }) => (
          <div key={name}>
            <Header>{name}</Header>
            {renderColorGroup(value)}
          </div>
        ))}
      </>
    );
  };

  return (
    <ModalWrapper>
      <ConditionalOverlay center condition={saving}>
        <CustomizerHeader>
          <ResetButton />
        </CustomizerHeader>
        <ModalContent>
          <ColorsWrapper>
            {schemes.map(({ name, description, colors }) => (
              <div key={name}>
                <Header>{name}</Header>
                <div style={{ fontSize: '12px' }}>{description}</div>
                {renderColorGroup(colors)}
              </div>
            ))}
          </ColorsWrapper>
        </ModalContent>
        <ModalFooter
          {...(hasChanges
            ? {
                primaryLabel: 'Publish & Close',
                onPrimaryClick: onSave,
                leftChildren: (
                  <Button
                    style={{ marginLeft: '10px' }}
                    dense
                    destructive
                    onClick={() => {
                      setCustomColors(() => originalCustomColors);
                      close();
                    }}
                  >
                    Discard Changes & Close
                  </Button>
                ),
                primaryDisabled: hasPendingChange || saving,
              }
            : {
                primaryLabel: 'Close',
                onPrimaryClick: close,
              })}
        />
      </ConditionalOverlay>
    </ModalWrapper>
  );
};

const CustomizerHeader = ({ children }: { children?: React.ReactNode }) => (
  <ModalHeader>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
      <span style={{ whiteSpace: 'nowrap' }}>Color Customizer</span>
      {children}
    </div>
  </ModalHeader>
);

const ResetButton = () => {
  const { customColors, setCustomColors } = useContext(ThemeContext);
  const hasCustomTheme = !isNil(customColors);

  return (
    <Tooltip content={`${getAppTitle()} defaults are selected`} isHidden={hasCustomTheme}>
      <Button disabled={!hasCustomTheme} onClick={() => setCustomColors(undefined)}>
        Reset To {getAppTitle()} Defaults
      </Button>
    </Tooltip>
  );
};

const CUSTOM_COLORS_SETTINGS: {
  [key in CustomColorsKeys]: EditableColorSetting<keyof VennColors[key]>;
} = {
  StudioSubjectColor: {
    displayName: 'Subject Based',
    description: (
      <>
        <div>
          These colors are the base palette. In addition to being used for subject-based charts, all other chart types
          should also select colors from this list whenever possible.
        </div>
        <div style={{ paddingTop: '5px' }}>
          For subject-based charts, each unique color reflects a different subject. Affected Formats and Blocks: Line
          and Bar Charts (Asset Growth Simulation, Time Series, Returns Distribution, Notable Historical Periods,
          Sensitivity Analysis), Scatter Plot (Performance and Risk), Donut (Portfolio Composition).
        </div>
        <div className="pt-[5px]">
          When more than 9 subjects need to be represented by colors in a single block, the palette below will loop at
          50% opacity for subjects 10–18, 33% opacity for subjects 19–27, and 25% opacity for subjects 28–30.
        </div>
      </>
    ),
    editableKeys: [
      { key: 'A1', displayName: 'Color 1' },
      { key: 'A2', displayName: 'Color 2' },
      { key: 'A3', displayName: 'Color 3' },
      { key: 'A4', displayName: 'Color 4' },
      { key: 'A5', displayName: 'Color 5' },
      { key: 'A6', displayName: 'Color 6' },
      { key: 'A7', displayName: 'Color 7' },
      { key: 'A8', displayName: 'Color 8' },
      { key: 'A9', displayName: 'Color 9' },
    ],
  },
  DivergingColor: {
    displayName: 'Magnitude-Sensitive',
    description: `The colors reflect the magnitude of a value e.g. Correlation, Exposure. Colors in these charts are selected from 2 colors configured by the user.
    Affected visualizations and blocks: Heatmap: (Correlations, Factor Trend, Geography), Bar (Factor Bar Charts)
    `,
    editableKeys: [
      { key: 'B5', displayName: 'Positive values (cool)' },
      { key: 'A5', displayName: 'Negative values (warm)' },
      { key: 'MID', displayName: 'Midpoint (neutral)' },
    ],
  },
  HoldingsColor: {
    displayName: 'Holdings',
    description:
      'Each Asset Class and Sector is assigned a specific color that is consistent in every view and report in Studio and Report Lab.',
  },
  CashFlowColor: {
    displayName: 'Private Asset Bar Charts',
    description: `Historical, Projected, and Typical are three items always shown in cash flow charts. Each are assigned a color.
    Affected Visualization and Blocks: Private Asset - Cash Flow, Private Asset - NAV, Private Asset - Distributions, Private Asset - Contributions.`,
  },
  PeerGroupColor: {
    displayName: 'Peer Group Analytics',
    description:
      'The single Box Chart color determines the gradient and percentile colors in both versions of the Peer Group Analytics block.',
    editableKeys: [{ key: 'GradientHigh', displayName: 'Box Chart' }],
  },
};

const getSchemes = (theme: VennColors): ColorScheme[] => {
  return CUSTOM_COLORS_KEYS.map((key) => {
    const setting = CUSTOM_COLORS_SETTINGS[key];
    return {
      name: setting.displayName,
      description: setting.description,
      colors: getColorGroup(theme[key], [key], setting.editableKeys),
    };
  });
};

const getColorGroup = (
  subTheme: object,
  path: string[],
  editableKeys: { key: string; displayName?: string }[] | undefined,
): ColorGroup => {
  const colorsKeys: string[] = Object.keys(subTheme)
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    .filter((key) => typeof subTheme[key] === 'string')
    .sort((a, b) => {
      const editableKeyIndexA = editableKeys?.findIndex((x) => x.key === a) ?? -1;
      const editableKeyIndexB = editableKeys?.findIndex((x) => x.key === b) ?? -1;
      return editableKeyIndexA - editableKeyIndexB;
    });

  // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
  const childrenKeys: string[] = Object.keys(subTheme).filter((key) => typeof subTheme[key] === 'object');
  return {
    colors: colorsKeys
      .map((key) => ({ key, name: editableKeys?.find((x) => x.key === key)?.displayName ?? key, path: [...path, key] }))
      .filter(({ key }) => editableKeys === undefined || editableKeys.find((x) => x.key === key)),
    children: childrenKeys.map((key) => ({
      name: key,
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      value: getColorGroup(subTheme[key], [...path, key], editableKeys),
    })),
  };
};

const ModalWrapper = styled.div`
  position: absolute;
  bottom: 40px;
  right: 40px;
  width: 600px;
  background-color: ${GetColor.White};
  z-index: ${ZIndex.Modal};
  box-shadow: 0 5px 10px 0 ${GetColor.GreyScale.Grey60};
  padding-top: 10px;
`;

const Header = styled(Headline3)`
  margin-top: 10px;
`;

const Inline = styled.div<{ order: number }>`
  padding-bottom: 5px;
  width: 50%;
  order: ${({ order }) => order};
`;

const ColorsWrapper = styled.div`
  height: 500px;
  overflow-y: scroll;
`;
